import React, { useState, useCallback, useEffect } from "react";
import {
  Button,
  Input,
  Label,
  Text,
  Card,
  Spinner,
  Dropdown,
  Option,
  Textarea,
  Checkbox,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Search24Regular,
  Building24Regular,
  ChevronUp24Regular,
  ChevronDown24Regular,
} from "@fluentui/react-icons";
import { useCreateRecord } from "../../hooks/useCreateRecord";
import { dataverseClient } from "../../services/dataverseClient";

const useStyles = makeStyles({
  form: { display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" },
  field: { display: "flex", flexDirection: "column", gap: "4px" },
  row: { display: "flex", gap: "6px" },
  halfField: { flex: 1, display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 },
  error: { color: tokens.colorStatusDangerForeground1 },
  actions: { display: "flex", gap: "8px", marginTop: "4px" },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "6px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: "4px",
  },
  searchRow: { display: "flex", gap: "4px", alignItems: "flex-end" },
  resultCard: {
    padding: "6px 8px",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selectedAccount: {
    padding: "6px 8px",
    borderColor: tokens.colorBrandStroke1,
    borderWidth: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultIcon: { display: "flex", alignItems: "center", gap: "6px" },
  resultMeta: { color: tokens.colorNeutralForeground3, fontSize: "12px" },
  checkboxGroup: { display: "flex", flexDirection: "column", gap: "2px", maxHeight: "150px", overflowY: "auto" },
});

interface PicklistOption {
  value: number;
  label: string;
}

interface CreateLeadFormProps {
  prefillEmail?: string;
  prefillName?: string;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateLeadForm: React.FC<CreateLeadFormProps> = ({
  prefillEmail,
  prefillName,
  onClose,
  onCreated,
}) => {
  const styles = useStyles();
  const { createRecord, creating, error } = useCreateRecord();

  // Picklist options (fetched on mount)
  const [facilityTypeOptions, setFacilityTypeOptions] = useState<PicklistOption[]>([]);
  const [hospitalTypeOptions, setHospitalTypeOptions] = useState<PicklistOption[]>([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState<PicklistOption[]>([]);
  const [systemsOptions, setSystemsOptions] = useState<PicklistOption[]>([]);
  const [featuresOptions, setFeaturesOptions] = useState<PicklistOption[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [facility, hospital, source, systems, features] = await Promise.all([
          dataverseClient.getPicklistOptions("lead", "westcom_facilitytype"),
          dataverseClient.getPicklistOptions("lead", "westcom_hospitaltype"),
          dataverseClient.getPicklistOptions("lead", "westcom_leadsource"),
          dataverseClient.getPicklistOptions("lead", "westcom_systems", true),
          dataverseClient.getPicklistOptions("lead", "westcom_features", true),
        ]);
        setFacilityTypeOptions(facility);
        setHospitalTypeOptions(hospital);
        setLeadSourceOptions(source);
        setSystemsOptions(systems);
        setFeaturesOptions(features);
      } catch (err) {
        console.error("Failed to load picklist options:", err);
      }
    };
    load();
  }, []);

  const nameParts = prefillName?.split(" ") ?? [];

  // --- End User Info fields ---
  const [subject, setSubject] = useState("");
  const [firstname, setFirstname] = useState(nameParts[0] ?? "");
  const [lastname, setLastname] = useState(nameParts.slice(1).join(" ") ?? "");
  const [facilityType, setFacilityType] = useState("");
  const [hospitalType, setHospitalType] = useState("");
  const [companyname, setCompanyname] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [idn, setIdn] = useState("");
  const [gpo, setGpo] = useState("");
  const [leadSource, setLeadSource] = useState("");

  // Facility (Account) lookup
  const [accountQuery, setAccountQuery] = useState("");
  const [accountResults, setAccountResults] = useState<any[]>([]);
  const [searchingAccounts, setSearchingAccounts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<{ id: string; name: string } | null>(null);

  // Distributor (Account) lookup
  const [distributorQuery, setDistributorQuery] = useState("");
  const [distributorResults, setDistributorResults] = useState<any[]>([]);
  const [searchingDistributors, setSearchingDistributors] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState<{ id: string; name: string } | null>(null);

  // --- Project Info fields ---
  const [estimatedForecast, setEstimatedForecast] = useState("");
  const [selectedSystems, setSelectedSystems] = useState<number[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
  const [forecastedStartDate, setForecastedStartDate] = useState("");
  const [probability, setProbability] = useState("");
  const [demoScheduledDate, setDemoScheduledDate] = useState("");
  const [proposalSubmittedDate, setProposalSubmittedDate] = useState("");
  const [numberOfBeds, setNumberOfBeds] = useState("");
  const [nursingUnits, setNursingUnits] = useState("");
  const [description, setDescription] = useState("");

  // Collapsible sections
  const [showProjectInfo, setShowProjectInfo] = useState(false);

  const handleAccountSearch = useCallback(async () => {
    if (!accountQuery.trim()) return;
    setSearchingAccounts(true);
    try {
      const results = await dataverseClient.searchAccounts(accountQuery.trim());
      setAccountResults(results);
    } catch {
      setAccountResults([]);
    } finally {
      setSearchingAccounts(false);
    }
  }, [accountQuery]);

  const handleAccountKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleAccountSearch();
  };

  const handleDistributorSearch = useCallback(async () => {
    if (!distributorQuery.trim()) return;
    setSearchingDistributors(true);
    try {
      const results = await dataverseClient.searchDistributors(distributorQuery.trim());
      setDistributorResults(results);
    } catch {
      setDistributorResults([]);
    } finally {
      setSearchingDistributors(false);
    }
  }, [distributorQuery]);

  const handleDistributorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleDistributorSearch();
  };

  const toggleMultiSelect = (
    value: number,
    selected: number[],
    setSelected: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    try {
      const data: Record<string, any> = {
        firstname,
        lastname,
        ...(subject && { subject }),
        ...(companyname && { companyname }),
        ...(facilityType && { westcom_facilitytype: parseInt(facilityType) }),
        ...(hospitalType && { westcom_hospitaltype: parseInt(hospitalType) }),
        ...(leadSource && { westcom_leadsource: parseInt(leadSource) }),
        ...(street && { address1_line1: street }),
        ...(city && { address1_city: city }),
        ...(state && { address1_stateorprovince: state }),
        ...(zip && { address1_postalcode: zip }),
        ...(idn && { westcom_idn: idn }),
        ...(gpo && { westcom_gpo: gpo }),
        ...(estimatedForecast && { westcom_estimatedforecastamount: parseFloat(estimatedForecast) }),
        ...(forecastedStartDate && { westcom_forecastedstartdate: forecastedStartDate }),
        ...(probability && { westcom_probability: parseInt(probability) }),
        ...(demoScheduledDate && { westcom_demoscheduleddate: demoScheduledDate }),
        ...(proposalSubmittedDate && { westcom_proposalsubmitteddate: proposalSubmittedDate }),
        ...(numberOfBeds && { westcom_numberofbeds: parseInt(numberOfBeds) }),
        ...(nursingUnits && { westcom_nursingunits: nursingUnits }),
        ...(description && { description }),
        ...(selectedSystems.length > 0 && { westcom_systems: selectedSystems.join(",") }),
        ...(selectedFeatures.length > 0 && { westcom_features: selectedFeatures.join(",") }),
      };
      if (selectedAccount) {
        data["parentaccountid@odata.bind"] = `/accounts(${selectedAccount.id})`;
      }
      if (selectedDistributor) {
        data["westcom_Distributor@odata.bind"] = `/accounts(${selectedDistributor.id})`;
      }
      await createRecord("lead", data);
      onCreated();
    } catch {
      // error is surfaced via hook
    }
  };

  return (
    <div className={styles.form}>
      <Text weight="semibold" size={400}>Create New Lead</Text>

      {/* ===== END USER INFO ===== */}
      <div className={styles.field}>
        <Label>Title</Label>
        <Input placeholder="e.g., Interest in nurse call system" value={subject} onChange={(_, d) => setSubject(d.value)} />
      </div>

      <div className={styles.row}>
        <div className={styles.halfField}>
          <Label>First Name</Label>
          <Input value={firstname} onChange={(_, d) => setFirstname(d.value)} />
        </div>
        <div className={styles.halfField}>
          <Label>Last Name</Label>
          <Input value={lastname} onChange={(_, d) => setLastname(d.value)} />
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.halfField}>
          <Label>Facility Type</Label>
          <Dropdown
            placeholder="Select..."
            value={facilityTypeOptions.find((o) => String(o.value) === facilityType)?.label ?? ""}
            onOptionSelect={(_, d) => setFacilityType(d.optionValue ?? "")}
          >
            {facilityTypeOptions.map((o) => (
              <Option key={o.value} value={String(o.value)}>{o.label}</Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.halfField}>
          <Label>Hospital Type</Label>
          <Dropdown
            placeholder="Select..."
            value={hospitalTypeOptions.find((o) => String(o.value) === hospitalType)?.label ?? ""}
            onOptionSelect={(_, d) => setHospitalType(d.optionValue ?? "")}
          >
            {hospitalTypeOptions.map((o) => (
              <Option key={o.value} value={String(o.value)}>{o.label}</Option>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* Facility (Account) Lookup */}
      <div className={styles.field}>
        <Label>Facility (Lookup)</Label>
        {selectedAccount ? (
          <Card className={styles.selectedAccount}>
            <div className={styles.resultIcon}>
              <Building24Regular />
              <Text weight="semibold">{selectedAccount.name}</Text>
            </div>
            <Button
              size="small"
              appearance="subtle"
              onClick={() => {
                setSelectedAccount(null);
                setAccountQuery("");
                setAccountResults([]);
              }}
            >
              Change
            </Button>
          </Card>
        ) : (
          <>
            <div className={styles.searchRow}>
              <Input
                placeholder="Search for facility..."
                value={accountQuery}
                onChange={(_, d) => setAccountQuery(d.value)}
                onKeyDown={handleAccountKeyDown}
                style={{ flex: 1 }}
              />
              <Button
                icon={<Search24Regular />}
                onClick={handleAccountSearch}
                disabled={searchingAccounts || !accountQuery.trim()}
                title="Search accounts"
              />
            </div>
            {searchingAccounts && <Spinner size="tiny" label="Searching..." />}
            {accountResults.map((a) => (
              <Card
                key={a.accountid}
                className={styles.resultCard}
                onClick={() => {
                  setSelectedAccount({ id: a.accountid, name: a.name });
                  setAccountResults([]);
                }}
              >
                <div className={styles.resultIcon}>
                  <Building24Regular />
                  <div>
                    <Text weight="semibold" size={300}>{a.name}</Text>
                    {(a.accountnumber || a.emailaddress1) && (
                      <Text size={200} className={styles.resultMeta} block>
                        {[a.accountnumber, a.emailaddress1].filter(Boolean).join(" | ")}
                      </Text>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {!searchingAccounts && accountQuery && accountResults.length === 0 && (
              <Text size={200}>No accounts found.</Text>
            )}
          </>
        )}
      </div>

      <div className={styles.field}>
        <Label>Facility Name</Label>
        <Input value={companyname} onChange={(_, d) => setCompanyname(d.value)} />
      </div>

      <div className={styles.field}>
        <Label>Street Address</Label>
        <Input value={street} onChange={(_, d) => setStreet(d.value)} />
      </div>
      <div className={styles.row}>
        <div className={styles.halfField}>
          <Label>City</Label>
          <Input value={city} onChange={(_, d) => setCity(d.value)} />
        </div>
        <div className={styles.halfField}>
          <Label>State</Label>
          <Input value={state} onChange={(_, d) => setState(d.value)} />
        </div>
      </div>
      <div className={styles.field}>
        <Label>Postal Code</Label>
        <Input value={zip} onChange={(_, d) => setZip(d.value)} />
      </div>

      <div className={styles.row}>
        <div className={styles.halfField}>
          <Label>IDN</Label>
          <Input value={idn} onChange={(_, d) => setIdn(d.value)} />
        </div>
        <div className={styles.halfField}>
          <Label>GPO</Label>
          <Input value={gpo} onChange={(_, d) => setGpo(d.value)} />
        </div>
      </div>

      <div className={styles.field}>
        <Label>Lead Source</Label>
        <Dropdown
          placeholder="Select source..."
          value={leadSourceOptions.find((o) => String(o.value) === leadSource)?.label ?? ""}
          onOptionSelect={(_, d) => setLeadSource(d.optionValue ?? "")}
        >
          {leadSourceOptions.map((o) => (
            <Option key={o.value} value={String(o.value)}>{o.label}</Option>
          ))}
        </Dropdown>
      </div>

      {/* Distributor (Account) Lookup */}
      <div className={styles.field}>
        <Label>Distributor</Label>
        {selectedDistributor ? (
          <Card className={styles.selectedAccount}>
            <div className={styles.resultIcon}>
              <Building24Regular />
              <Text weight="semibold">{selectedDistributor.name}</Text>
            </div>
            <Button
              size="small"
              appearance="subtle"
              onClick={() => {
                setSelectedDistributor(null);
                setDistributorQuery("");
                setDistributorResults([]);
              }}
            >
              Change
            </Button>
          </Card>
        ) : (
          <>
            <div className={styles.searchRow}>
              <Input
                placeholder="Search for distributor..."
                value={distributorQuery}
                onChange={(_, d) => setDistributorQuery(d.value)}
                onKeyDown={handleDistributorKeyDown}
                style={{ flex: 1 }}
              />
              <Button
                icon={<Search24Regular />}
                onClick={handleDistributorSearch}
                disabled={searchingDistributors || !distributorQuery.trim()}
                title="Search distributors"
              />
            </div>
            {searchingDistributors && <Spinner size="tiny" label="Searching..." />}
            {distributorResults.map((a) => (
              <Card
                key={a.accountid}
                className={styles.resultCard}
                onClick={() => {
                  setSelectedDistributor({ id: a.accountid, name: a.name });
                  setDistributorResults([]);
                }}
              >
                <div className={styles.resultIcon}>
                  <Building24Regular />
                  <div>
                    <Text weight="semibold" size={300}>{a.name}</Text>
                    {(a.accountnumber || a.emailaddress1) && (
                      <Text size={200} className={styles.resultMeta} block>
                        {[a.accountnumber, a.emailaddress1].filter(Boolean).join(" | ")}
                      </Text>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {!searchingDistributors && distributorQuery && distributorResults.length === 0 && (
              <Text size={200}>No distributors found.</Text>
            )}
          </>
        )}
      </div>

      {/* ===== PROJECT INFO (collapsible) ===== */}
      <div
        className={styles.sectionHeader}
        onClick={() => setShowProjectInfo(!showProjectInfo)}
      >
        <Text weight="semibold" size={300}>Project Info</Text>
        {showProjectInfo ? <ChevronUp24Regular /> : <ChevronDown24Regular />}
      </div>
      {showProjectInfo && (
        <>
          <div className={styles.field}>
            <Label>Estimated Forecast Amount</Label>
            <Input type="number" placeholder="0.00" value={estimatedForecast} onChange={(_, d) => setEstimatedForecast(d.value)} />
          </div>

          {systemsOptions.length > 0 && (
            <div className={styles.field}>
              <Label>Systems</Label>
              <div className={styles.checkboxGroup}>
                {systemsOptions.map((o) => (
                  <Checkbox
                    key={o.value}
                    label={o.label}
                    checked={selectedSystems.includes(o.value)}
                    onChange={() => toggleMultiSelect(o.value, selectedSystems, setSelectedSystems)}
                  />
                ))}
              </div>
            </div>
          )}

          {featuresOptions.length > 0 && (
            <div className={styles.field}>
              <Label>Features</Label>
              <div className={styles.checkboxGroup}>
                {featuresOptions.map((o) => (
                  <Checkbox
                    key={o.value}
                    label={o.label}
                    checked={selectedFeatures.includes(o.value)}
                    onChange={() => toggleMultiSelect(o.value, selectedFeatures, setSelectedFeatures)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className={styles.row}>
            <div className={styles.halfField}>
              <Label>Forecasted Start Date</Label>
              <Input type="date" value={forecastedStartDate} onChange={(_, d) => setForecastedStartDate(d.value)} />
            </div>
            <div className={styles.halfField}>
              <Label>Probability (%)</Label>
              <Input type="number" placeholder="0" value={probability} onChange={(_, d) => setProbability(d.value)} />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.halfField}>
              <Label>Demo Scheduled Date</Label>
              <Input type="date" value={demoScheduledDate} onChange={(_, d) => setDemoScheduledDate(d.value)} />
            </div>
            <div className={styles.halfField}>
              <Label>Proposal Submitted Date</Label>
              <Input type="date" value={proposalSubmittedDate} onChange={(_, d) => setProposalSubmittedDate(d.value)} />
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.halfField}>
              <Label>Number of Beds</Label>
              <Input type="number" placeholder="0" value={numberOfBeds} onChange={(_, d) => setNumberOfBeds(d.value)} />
            </div>
            <div className={styles.halfField}>
              <Label>Nursing Units</Label>
              <Input value={nursingUnits} onChange={(_, d) => setNursingUnits(d.value)} />
            </div>
          </div>

          <div className={styles.field}>
            <Label>Description</Label>
            <Textarea
              placeholder="Additional notes..."
              value={description}
              onChange={(_, d) => setDescription(d.value)}
              rows={3}
            />
          </div>
        </>
      )}

      {error && <Text className={styles.error} size={200}>{error.message}</Text>}

      <div className={styles.actions}>
        <Button appearance="primary" onClick={handleSubmit} disabled={creating}>
          {creating ? "Creating..." : "Create Lead"}
        </Button>
        <Button appearance="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
};
